// Titles and definitions shown via the info button next to form fields
// throughout the app. Organised by feature area so any dialog/page can add
// its own section here and reuse the shared `InfoButton` / `FieldWithInfo`
// components (see `components/ui/infoButton.jsx` and
// `components/ui/fieldWithInfo.jsx`).
const FIELD_INFO = {
  gameFormat: {
    name: {
      title: 'Name',
      description:
        'A short, recognisable name for this game format. Shown wherever organisers choose a format for an event or game.'
    },
    description: {
      title: 'Description',
      description:
        'Optional extra detail about this format, to help other organisers understand when to use it.'
    },
    gameType: {
      title: 'Game Type',
      description:
        'The number of players per team - Singles (1 player), Pairs (2), Triples (3) or Fours (4). This determines how many bowls each player delivers per end.'
    },
    numBowls: {
      title: 'Number of Bowls',
      description: 'How many bowls each player delivers per end.'
    },
    rounds: {
      title: 'Rounds',
      description: 'The number of rounds played across the event for this game format.'
    },
    ends: {
      title: 'Ends',
      description:
        'The fixed number of ends played per skin/set. Leave as 0 to play purely to a Max Score. Ends and Max Score can both be set - the game then ends on whichever is reached first.'
    },
    totalEnds: {
      title: 'Number of Ends Total',
      description:
        'The total number of ends played across the whole game (all skins/sets combined). The ends per skin/set is calculated automatically by dividing the total by the number of skins/sets.'
    },
    multiplier: {
      title: 'Use Multiplier',
      description:
        'Enables a "power end" style score multiplier that a team can activate once per game to multiply the points scored on that end.'
    },
    extraEndGamePoint: {
      title: 'Per-End Bonus Point',
      description:
        'Extra game points awarded to whichever team wins each end, on top of the normal Win/Draw/Loss points. If an end is drawn, the point is split evenly between both teams.'
    },
    sets: {
      title: 'Sets',
      description: 'The number of sets played in the game, for set or skins based formats.'
    },
    maxTotalPerSet: {
      title: 'Points Per Set',
      description: 'The number of shots/points needed to win an individual set.'
    },
    excludeSetPointInGamePoints: {
      title: 'Exclude Set Point from Game Points',
      description:
        'When enabled, points earned for winning a set are not added to the overall game points total.'
    },
    setAsSkin: {
      title: 'Skins or Sets',
      description:
        'Choose how the game is split up. Skins treats each set as a "skin", where only the shots scored on the deciding end of the set count. Sets plays each set normally.'
    },
    maxScore: {
      title: 'Max Score',
      description:
        'The score a team must reach to win the game. Leave as 0 to play to a fixed number of Ends instead. Ends and Max Score can both be set - the game then ends on whichever is reached first, and if the ends run out, the team with the higher score wins.'
    },
    winPoints: {
      title: 'Win Points',
      description: 'The number of game points awarded to a team for winning the game.'
    },
    drawPoints: {
      title: 'Draw Points',
      description:
        'The number of game points awarded to each team when the game ends in a draw. Setting this above 0 disables the Tiebreaker step, since draws are allowed instead of being played off.'
    },
    lossPoints: {
      title: 'Loss Points',
      description: 'The number of game points awarded to a team for losing the game.'
    },
    playoffs: {
      title: 'Use Tiebreaker',
      description:
        'Enables a tiebreaker (playoff) end to decide a winner when scores are level at the end of the game. Not available if Draw Points is greater than 0.'
    },
    playoffMaxPoints: {
      title: 'Tiebreaker Max Points',
      description: 'The maximum number of points that can be scored during the tiebreaker end.'
    },
    playoffPoint: {
      title: 'Tiebreaker Point',
      description: 'The number of game points awarded to the team that wins the tiebreaker end.'
    },
    excludePlayoffPointInGamePoints: {
      title: 'Exclude Tiebreaker Point from Game Points',
      description:
        'When enabled, the tiebreaker point is not added to the overall game points total.'
    },
    includeHandicap: {
      title: 'Include Handicap',
      description: "Applies each player or team's handicap to adjust scores for this format."
    },
    sections: {
      title: 'Sections',
      description: 'The number of sections players are split into for this game format.'
    }
  },
  event: {
    name: {
      title: 'Name',
      description: 'The name of the event, shown to players and organisers throughout the app.'
    },
    description: {
      title: 'Description',
      description:
        'Optional extra detail about the event, such as format notes or entry conditions.'
    },
    startDate: {
      title: 'Start Date',
      description: 'The date the event begins.'
    },
    endDate: {
      title: 'End Date',
      description:
        'The date the event ends. For single-day events, this can be the same as the start date.'
    },
    venueNotApplicable: {
      title: 'Venue Not Applicable',
      description:
        'Enable this if the event has no fixed venue (for example, an away/travelling event). Hides the venue and green setup fields.'
    },
    useMultipleVenues: {
      title: 'Use Multiple Clubs',
      description:
        'Enable this if the event is played across more than one club/venue. Lets you choose a list of clubs instead of a single venue.'
    },
    venue: {
      title: 'Venue',
      description: 'The club/venue where the event is played.'
    },
    venueGreensCount: {
      title: 'Greens',
      description: 'The number of greens available at the venue for this event.'
    },
    venueRinksPerGreen: {
      title: 'Rinks Per Green',
      description: 'The number of rinks on each green at the venue.'
    },
    locations: {
      title: 'Choose Clubs',
      description: 'The clubs/venues involved in this event, when using multiple clubs.'
    },
    greenSetup: {
      title: 'Green Setup',
      description:
        'Choose which rinks on each green are available for this event. Toggle a rink off to exclude it from being used when generating draws/games.'
    },
    gameFormat: {
      title: 'Game Format',
      description:
        'The game format used for this event, defining rules like ends, scoring and points. Choose an existing format or create a new one.'
    },
    useGroups: {
      title: 'Use Groups',
      description:
        'Enable this to organise teams/players into named, colour-coded groups (for example, divisions or grades) that can be used when creating teams.'
    },
    hasBracketOnly: {
      title: 'Bracket Only',
      description:
        'Enable this if the event is a knockout bracket only, with no round robin or sectional play beforehand.'
    },
    seedingTemplate: {
      title: 'Seeding Template',
      description: 'Optional predefined seeding order used to place teams into the bracket.'
    },
    roundPublishMethod: {
      title: 'Round Publish Method',
      description:
        'Controls how rounds become available during the event - either automatically continuing to the next round, or waiting for an organiser to publish it.'
    },
    hasSections: {
      title: 'Sectional Play',
      description:
        'Enable this if the event is split into separate sections, each played independently.'
    },
    scoringMethod: {
      title: 'Scoring Method',
      description:
        'How scores can be entered for games in this event - end by end live scoring, final score per set/skin, or manual totals-only entry (total shots and total game points).'
    },
    publishLogs: {
      title: 'Publish the Logs',
      description: 'When enabled, the event logs are visible to players, not just organisers.'
    },
    publishScorecard: {
      title: 'Publish the Detailed Score Card',
      description:
        'When enabled, the detailed end-by-end scorecard is visible to players, not just organisers.'
    },
    publishEvent: {
      title: 'Publish the Event',
      description:
        'When enabled, games in this event appear in the public current games feed and the public player games lookup as soon as they start. Turn it off to keep this event out of those feeds.'
    }
  }
};

export default FIELD_INFO;
